
import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import { RootState } from "..";
import { UserLicenseTypeEnum } from "../../components/enums/userLicenseTypeEnum";
import { IAuthAccount } from "../../interfaces/IAuthTokenResultDto";
import { IUserProfile, IUserSettings, WelcomeFlag } from "../../interfaces/IUserProfile";
import { UserState } from "../../types";
import { typedAction } from "../helpers";
import { RESET_USER_ACCOUNT, SET_USER_ACCOUNT, SET_USER_PROFILE, SET_USER_SETTINGS } from "./actionTypes";
import emailMapper from "../../services/EmailMapper";
import LZString = require("lz-string");
import { logger } from "../../../../utilities/logger";
import { apiGetCompressedEmailToMetadataMapping, apiSetEmailToMetadataMapping, apiUpdateUserSettings } from "../../services/ApiService";
import { SubscriptionProductEnum } from "../../components/enums/SubscriptionProductEnum";

const initialState: UserState = {
  licenseType: UserLicenseTypeEnum.Expired,
  subscriptionProduct: SubscriptionProductEnum.DocubirdProfessional,
  oneDriveWebUrl: "",
  account: null,
  settings: {
    showRecent: true,
    showPinned: true,
    showShared: true,
  },
  tenantSaveOnSendEnabled: false,
  tenantAllowOpenInSharePoint: true,
  tenantEnablePII: false,
  tenantEnableApprovalHooks: false,
  isActivated: false,
  tenantOcrLanguage: "",
  tenantEnableRedaction: false
};

export const setUserProfile = (profile: IUserProfile) => {
  return typedAction(SET_USER_PROFILE, profile);
};

const setUserSettings = (settings: IUserSettings) => {
  return typedAction(SET_USER_SETTINGS, settings);
};

export const updateUserSettings = (accountId: string, settings: IUserSettings): ThunkAction<void, RootState, unknown, Action<string>> => (dispatch) => {
  //make api call with settings to db
  apiUpdateUserSettings(settings, accountId);
  //dispatch the userSetter so that it is updated in global state
  dispatch(setUserSettings(settings));
};

// Closes the first-run welcome for the session and clears the app's flag on the server. Only the
// one field is sent: updateUserSettings posts the whole object, rewriting unrelated settings.
export const dismissWelcome = (flag: WelcomeFlag): ThunkAction<void, RootState, unknown, Action<string>> => (dispatch, getState) => {
  const { account, settings } = getState().user;
  const nextSettings: IUserSettings = { ...settings };
  nextSettings[flag] = false;
  dispatch(setUserSettings(nextSettings));

  if (account?.accountId) {
    const change: IUserSettings = {};
    change[flag] = false;
    // apiUpdateUserSettings has already logged a failure (handleError) and rethrows non-auth
    // errors; caught here only so it is not unhandled. A failed request just means the welcome
    // shows again on the next launch.
    apiUpdateUserSettings(change, account.accountId)
      .catch(() => undefined);
  }
};

export const getEmailToMetaDataMapping = async (accountId: string, tenantEmailToMetadataMapping: string) => {
  try {
    const compressedUserMailMapping = await apiGetCompressedEmailToMetadataMapping(accountId);
    if (compressedUserMailMapping) {
      const userMailMappings: string = LZString.decompressFromBase64(compressedUserMailMapping);
      emailMapper.setMapping(userMailMappings);
    } 
    else if (tenantEmailToMetadataMapping) {
      const tenantMailMappings: string = LZString.decompressFromBase64(tenantEmailToMetadataMapping);
      emailMapper.setMapping(tenantMailMappings);
    }
  } catch (error) {
    logger(error, "user.ts (getEmailToMetaDataMapping)");
  }
};

export const setEmailToMetaDataMapping = async (accountId: string, emailMapping: string) => {
  emailMapper.updateMappingLists();
  const compressedData = LZString.compressToBase64(emailMapping);
  await apiSetEmailToMetadataMapping(accountId, compressedData);
};

export const setUserAccount = (account: IAuthAccount) => {
  return typedAction(SET_USER_ACCOUNT, account);
};

export const resetUserAccount = () => {
  return typedAction(RESET_USER_ACCOUNT);
};

type UserAction = ReturnType<typeof setUserAccount | typeof resetUserAccount | typeof setUserProfile | typeof setUserSettings>;

export function userReducer(state = initialState, action: UserAction): UserState {
  switch (action.type) {
    // case SET_USER_LICENSE_TYPE:
    //   return { ...state, licenseType: action.payload };
    case SET_USER_ACCOUNT:
      return { ...state, oneDriveWebUrl: action.payload?.oneDriveWebUrl, account: action.payload };
    case RESET_USER_ACCOUNT:
      return { ...initialState };
    case SET_USER_PROFILE: //logging in -> get profile -> set in redux
      return {
        ...state,
        licenseType: action.payload.userLicenseType,
        subscriptionProduct: action.payload.subscriptionProduct,
        settings: action.payload.settings,
        tenantSaveOnSendEnabled: action.payload.tenantSaveOnSendEnabled ?? false,
        tenantAllowOpenInSharePoint: action.payload.tenantAllowOpenInSharePoint ?? true,
        tenantEnablePII: action.payload.tenantEnablePII ?? false,
        tenantEnableApprovalHooks: action.payload.tenantEnableApprovalHooks ?? false,
        isActivated: action.payload.isActivated ?? false,
        tenantOcrLanguage: action.payload.tenantOcrLanguage,
        tenantEnableRedaction: action.payload.tenantEnableRedaction,
        isLoadedFromCache: action.payload.isLoadedFromCache ?? false
      };
    case SET_USER_SETTINGS: //when toggling between seeing pinned/recent/shared this is called. 
      return { ...state, settings: action.payload }
    default:
      return state;
  }
}
